/**
 * Syncs the PDP with the purchase option selected in the Appstle
 * subscription widget (app block). Two jobs:
 *
 * 1. Price display — Appstle's own price sync only targets Dawn's default
 *    `.price` markup, so the theme's `.wonderly-product-price__primary`
 *    would otherwise keep showing the one-time price after a plan is
 *    selected. The formatted amount is copied from the checked option row,
 *    so plan discounts and currency conversion (Transcy) come along for
 *    free. Unit-priced Grocerist products are skipped — their primary line
 *    is $/unit (see data-wonderly-unit-priced in wonderly-product-price.liquid).
 *
 * 2. Selling plan → cart form — the widget renders outside the product
 *    <form>, and on this store Appstle Subscriptions' own selling-plan
 *    fetch interceptor is DISABLED by its bundle_rules gate: when Appstle
 *    Bundles has active bundle rules (window._ABConfig.bundle_rules), the
 *    Subscriptions script skips fetchAddJsSellingPlanInterCeptor() and the
 *    Bundles interceptor never reads the widget — so every add-to-cart
 *    went out as a one-time purchase. We mirror the checked plan into a
 *    hidden selling_plan input inside the product form so Dawn's
 *    FormData-based submit (product-form.js) carries it natively. The
 *    Bundles interceptor passes an existing selling_plan through untouched
 *    (verified), and if Appstle later re-enables their interceptor it
 *    appends the same value — harmless either way.
 */
(() => {
  const priceTarget = () => {
    // Skip unit-priced ($/unit primary) and B2B wholesale ("Your Price:" +
    // struck retail) displays — overwriting their primary would desync them.
    const wrapper = document.querySelector(
      '.wonderly-pdp__price .wonderly-product-price:not([data-wonderly-unit-priced]):not([data-wonderly-b2b])'
    );
    return wrapper ? wrapper.querySelector('.wonderly-product-price__primary') : null;
  };

  const syncPrice = () => {
    const target = priceTarget();
    if (!target) return;
    const checked = document.querySelector('.appstle_sub_widget input[name="selling_plan"]:checked');
    if (!checked) return;
    const option = checked.closest('.appstle_subscription_wrapper_option');
    const amount = option && option.querySelector('.appstle_subscription_amount');
    const text = amount ? amount.textContent.trim() : '';
    if (text && /\d/.test(text) && target.textContent.trim() !== text) {
      target.textContent = text;
    }
  };

  const syncSellingPlanField = () => {
    const widget = document.querySelector('.appstle_sub_widget');
    if (!widget) return;
    // The main PDP product form (not the installment form, not quick-add)
    const form = document.querySelector('product-form form[action*="/cart/add"]');
    if (!form) return;
    const checked = widget.querySelector('input[name="selling_plan"]:checked');
    const planId = checked ? checked.value : '';
    let hidden = form.querySelector('input[type="hidden"][name="selling_plan"][data-wonderly-plan-sync]');
    if (planId) {
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'selling_plan';
        hidden.setAttribute('data-wonderly-plan-sync', '');
        form.appendChild(hidden);
      }
      hidden.value = planId;
    } else if (hidden) {
      // One-time purchase selected — drop the field entirely
      hidden.remove();
    }
  };

  const syncAll = () => {
    syncPrice();
    syncSellingPlanField();
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches) return;
    if (event.target.matches('.appstle_sub_widget input[name="selling_plan"], .appstle_sub_widget select')) {
      syncAll();
    }
  });

  // Appstle builds and rebuilds the widget asynchronously (initial paint,
  // variant changes) — watch its app block and re-apply after each render.
  const observeWidget = () => {
    const block = document.querySelector('[id*="appstle_subscription_product_page_widget"]');
    if (!block) return false;
    new MutationObserver(syncAll).observe(block, { childList: true, subtree: true, characterData: true });
    syncAll();
    return true;
  };

  if (!observeWidget()) {
    document.addEventListener('DOMContentLoaded', observeWidget, { once: true });
  }

  // Dawn re-renders #price-<section> with the one-time price on variant
  // change; re-apply the selected plan's price after it settles.
  if (window.subscribe && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(syncAll, 0));
  }
})();
